// Reunderwriting Module (Tier 3) PR 3 — Closing tab.
//
// Surface:
//   - Pre-approval gate: case must be in Closing stage + outcome must be
//     finalized + Pass. Underwriter-only "Advance to Closing" CTA on the
//     Underwriting tab moves the case here; see ReunderwritingCaseDetailPage.
//   - Downstream-loan-effect confirmation panel: explains exactly what
//     will happen to the subject loan (status update / obligor swap /
//     retire-and-create-new / create-related-new) per the seeded
//     ReunderwritingEventConfig.DownstreamLoanEffect.
//   - Approval form: collects the per-effect inputs (closing date, new
//     buyer, sale price, etc.) and POSTs to /approve.
//   - Post-approval result panel: shows the created/updated loan IDs +
//     deep links back, and confirms the obligations emitted.

import { useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Card } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useDateFormatter } from "@/utils/datetime";
import {
  useApproveReunderwritingCase,
  useReunderwritingEventTypes,
  useReunderwritingOutcome,
} from "@/hooks/reunderwriting/useReunderwriting";
import {
  DOWNSTREAM_LOAN_EFFECT_LABEL,
  type DownstreamLoanEffect,
  type ReunderwritingApprovalRequest,
  type ReunderwritingApprovalResult,
  type ReunderwritingCaseDto,
} from "@/types/reunderwriting";

interface Props {
  rcase: ReunderwritingCaseDto;
}

export function ReunderwritingClosingTab({ rcase }: Props) {
  const outcome = useReunderwritingOutcome(rcase.id);
  const events = useReunderwritingEventTypes();
  const approve = useApproveReunderwritingCase(rcase.id);
  const toast = useToast();
  const [result, setResult] = useState<ReunderwritingApprovalResult | null>(null);

  const cfg = events.data?.find((e) => e.eventTypeCode === rcase.eventTypeCode);
  const effect: DownstreamLoanEffect | undefined = cfg?.downstreamLoanEffect;

  const stageOk = rcase.stage === "Closing" || rcase.stage === "Closed";
  if (!stageOk) {
    return (
      <Card>
        <h3 className="font-display text-h3 m-0">Closing locked</h3>
        <p className="text-small text-gray-mid m-0 mt-space-1">
          The Closing tab unlocks when the underwriter advances the case from
          Underwriting → Closing on the <strong>Underwriting</strong> tab,
          after the combined outcome has been finalized as Pass.
        </p>
        <p className="text-caption text-gray-mid m-0 mt-space-2">
          Current stage: {rcase.stage}.
        </p>
      </Card>
    );
  }

  const alreadyClosed = rcase.stage === "Closed" || !!result;
  const outcomeReady = outcome.data?.isFinalized && outcome.data?.overallOutcome === "Pass";

  return (
    <div className="flex flex-col gap-space-3" data-testid="reunderwriting-closing-tab">
      <Card>
        <h3 className="font-display text-h3 m-0">Closing — Downstream Loan Effect</h3>
        <p className="text-small text-gray-mid m-0 mt-space-1">
          Event type: <strong>{rcase.eventTypeCode}</strong>
          {cfg && (
            <>
              {" · "}Authority: <strong>{cfg.authority}</strong>
            </>
          )}
        </p>

        {effect ? (
          <div className="mt-space-3 rounded-md border border-gold/40 bg-gold/5 p-space-3">
            <p className="font-display text-h4 m-0 mb-space-1">
              When you approve, this case will:{" "}
              <Badge status="gold">{DOWNSTREAM_LOAN_EFFECT_LABEL[effect]}</Badge>
            </p>
            <EffectDescription effect={effect} subjectLoanNumber={rcase.subjectLoanNumber} />
            <p className="text-caption text-gray-mid mt-space-2 m-0">
              Subject loan:{" "}
              <Link
                to={`/portfolio-admin/loans/${rcase.subjectLoanId}`}
                className="text-gold-shadow hover:underline"
              >
                {rcase.subjectLoanNumber ?? rcase.subjectLoanId}
              </Link>
            </p>
          </div>
        ) : (
          <p className="text-caption text-gray-mid italic m-0 mt-space-2">
            Loading event type configuration…
          </p>
        )}
      </Card>

      {!outcomeReady && !alreadyClosed && (
        <Card>
          <p
            className="text-small text-status-error m-0"
            data-testid="closing-outcome-gate"
          >
            Finalize the combined outcome as <strong>Pass</strong> on the
            Underwriting tab before approving this case.
          </p>
        </Card>
      )}

      {result && (
        <ApprovalResultPanel result={result} rcase={rcase} />
      )}

      {!alreadyClosed && effect && outcomeReady && (
        <ApprovalForm
          effect={effect}
          busy={approve.isPending}
          onSubmit={async (body) => {
            try {
              const r = await approve.mutateAsync(body);
              setResult(r);
              toast.success(
                r.alreadyApplied
                  ? "Case was already approved; result re-fetched."
                  : "Case approved.",
              );
            } catch (err) {
              toast.error(extractErrorMessage(err));
            }
          }}
        />
      )}
    </div>
  );
}

function EffectDescription({
  effect,
  subjectLoanNumber,
}: {
  effect: DownstreamLoanEffect;
  subjectLoanNumber: string | null;
}) {
  const ln = subjectLoanNumber ?? "the subject loan";
  switch (effect) {
    case "StatusUpdateOnly":
      return (
        <p className="text-small m-0">
          Advance flags on {ln} (e.g. pool-eligibility). The loan terms are
          unchanged.
        </p>
      );
    case "ObligorSwap":
      return (
        <p className="text-small m-0">
          Swap the borrower-of-record on {ln} to the new entity / person.
          Loan terms unchanged.
        </p>
      );
    case "RetireAndCreateNew":
      return (
        <p className="text-small m-0">
          Retire {ln} (mark closed) and create a new loan reflecting the
          right-size terms. Existing obligations roll into the new loan per
          waterfall config.
        </p>
      );
    case "CreateRelatedNew":
      return (
        <p className="text-small m-0">
          Atomically obligor-swap {ln} <em>and</em> create a new related
          loan (resale spread note). Both operations succeed or roll back
          together.
        </p>
      );
  }
}

function ApprovalResultPanel({
  result,
  rcase,
}: {
  result: ReunderwritingApprovalResult;
  rcase: ReunderwritingCaseDto;
}) {
  const { dateTime } = useDateFormatter();
  return (
    <Card data-testid="reunderwriting-approval-result">
      <header className="flex items-center justify-between gap-space-2">
        <h3 className="font-display text-h3 m-0">Case approved</h3>
        <Badge status="finalized">{result.effect}</Badge>
      </header>
      <p className="text-small mt-space-2 m-0">
        Subject loan:{" "}
        <Link
          to={`/portfolio-admin/loans/${result.subjectLoanId}`}
          className="text-gold-shadow hover:underline"
        >
          {rcase.subjectLoanNumber ?? result.subjectLoanId}
        </Link>
      </p>
      {result.createdLoanId && (
        <p className="text-small m-0">
          New loan created:{" "}
          <Link
            to={`/portfolio-admin/loans/${result.createdLoanId}`}
            className="text-gold-shadow hover:underline"
          >
            {result.createdLoanNumber ?? result.createdLoanId}
          </Link>
        </p>
      )}
      {result.obligationIds.length > 0 && (
        <p className="text-caption text-gray-mid m-0 mt-space-1">
          {result.obligationIds.length} obligation(s) emitted to the
          waterfall engine.
        </p>
      )}
      <p className="text-caption text-gray-mid m-0 mt-space-1">
        Completed at {dateTime(result.completedAt)}.
        {result.alreadyApplied && " (No-op — case was already approved.)"}
      </p>
    </Card>
  );
}

function ApprovalForm({
  effect,
  busy,
  onSubmit,
}: {
  effect: DownstreamLoanEffect;
  busy: boolean;
  onSubmit: (body: ReunderwritingApprovalRequest) => Promise<void>;
}) {
  const [closingDate, setClosingDate] = useState<string>("");
  const [newBorrowerEntityId, setNewBorrowerEntityId] = useState("");
  const [newBuyerEntityName, setNewBuyerEntityName] = useState("");
  const [salePrice, setSalePrice] = useState<string>("");
  const [principalShortfall, setPrincipalShortfall] = useState<string>("");
  const [assumedAmount, setAssumedAmount] = useState<string>("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const needsClosingDate = effect !== "StatusUpdateOnly";
  const needsNewBorrower = effect === "ObligorSwap";
  const needsBuyerAndPricing =
    effect === "RetireAndCreateNew" || effect === "CreateRelatedNew";
  const needsShortfall = effect === "RetireAndCreateNew";
  const needsAssumed = effect === "CreateRelatedNew";

  function isValid(): boolean {
    if (needsClosingDate && !closingDate) return false;
    if (needsNewBorrower && !newBorrowerEntityId) return false;
    if (needsBuyerAndPricing && (!newBuyerEntityName || !salePrice)) return false;
    if (needsShortfall && !principalShortfall) return false;
    if (needsAssumed && !assumedAmount) return false;
    return true;
  }

  function buildBody(): ReunderwritingApprovalRequest {
    return {
      closingDate: closingDate || undefined,
      newBorrowerEntityId: newBorrowerEntityId || undefined,
      newBuyerEntityName: newBuyerEntityName || undefined,
      salePrice: salePrice ? Number(salePrice) : undefined,
      principalShortfall: principalShortfall ? Number(principalShortfall) : undefined,
      assumedAmount: assumedAmount ? Number(assumedAmount) : undefined,
    };
  }

  return (
    <Card>
      <h3 className="font-display text-h3 m-0 mb-space-2">Approval inputs</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3" data-testid="reunderwriting-approval-form">
        {needsClosingDate && (
          <label className="flex flex-col gap-space-1">
            <span className="text-label uppercase tracking-label text-gold-shadow">
              Closing date <span className="text-status-error">*</span>
            </span>
            <input
              type="date"
              className="border border-border-light rounded-md p-space-2 text-small"
              value={closingDate}
              onChange={(e) => setClosingDate(e.target.value)}
            />
          </label>
        )}
        {needsNewBorrower && (
          <label className="flex flex-col gap-space-1">
            <span className="text-label uppercase tracking-label text-gold-shadow">
              New borrower entity ID <span className="text-status-error">*</span>
            </span>
            <input
              className="border border-border-light rounded-md p-space-2 text-small font-mono"
              value={newBorrowerEntityId}
              onChange={(e) => setNewBorrowerEntityId(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
            />
          </label>
        )}
        {needsBuyerAndPricing && (
          <>
            <label className="flex flex-col gap-space-1">
              <span className="text-label uppercase tracking-label text-gold-shadow">
                New buyer entity name <span className="text-status-error">*</span>
              </span>
              <input
                className="border border-border-light rounded-md p-space-2 text-small"
                value={newBuyerEntityName}
                onChange={(e) => setNewBuyerEntityName(e.target.value)}
              />
            </label>
            <label className="flex flex-col gap-space-1">
              <span className="text-label uppercase tracking-label text-gold-shadow">
                Sale price (USD) <span className="text-status-error">*</span>
              </span>
              <input
                type="number"
                className="border border-border-light rounded-md p-space-2 text-small"
                value={salePrice}
                onChange={(e) => setSalePrice(e.target.value)}
              />
            </label>
          </>
        )}
        {needsShortfall && (
          <label className="flex flex-col gap-space-1">
            <span className="text-label uppercase tracking-label text-gold-shadow">
              Principal shortfall (USD) <span className="text-status-error">*</span>
            </span>
            <input
              type="number"
              className="border border-border-light rounded-md p-space-2 text-small"
              value={principalShortfall}
              onChange={(e) => setPrincipalShortfall(e.target.value)}
            />
          </label>
        )}
        {needsAssumed && (
          <label className="flex flex-col gap-space-1">
            <span className="text-label uppercase tracking-label text-gold-shadow">
              Assumed amount (USD) <span className="text-status-error">*</span>
            </span>
            <input
              type="number"
              className="border border-border-light rounded-md p-space-2 text-small"
              value={assumedAmount}
              onChange={(e) => setAssumedAmount(e.target.value)}
            />
          </label>
        )}
      </div>

      <div className="mt-space-3 flex items-center justify-end gap-space-2">
        <Button
          onClick={() => setConfirmOpen(true)}
          disabled={busy || !isValid()}
          data-testid="reunderwriting-approve-button"
        >
          Approve & fire downstream effect
        </Button>
      </div>

      {confirmOpen && (
        <ConfirmModal
          effect={effect}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={async () => {
            await onSubmit(buildBody());
            setConfirmOpen(false);
          }}
          busy={busy}
        />
      )}
    </Card>
  );
}

function ConfirmModal({
  effect,
  onCancel,
  onConfirm,
  busy,
}: {
  effect: DownstreamLoanEffect;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
  busy: boolean;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Confirm approval"
      className="fixed inset-0 z-50 bg-onyx/40 flex items-center justify-center p-space-4"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      data-testid="reunderwriting-approve-confirm"
    >
      <div className="bg-white-pure rounded-md shadow-xl w-full max-w-[480px] p-space-4 flex flex-col gap-space-3">
        <h3 className="font-display text-h3 m-0">Confirm approval</h3>
        <p className="text-small m-0">
          This will execute the <strong>{DOWNSTREAM_LOAN_EFFECT_LABEL[effect]}</strong>
          {" "}downstream effect on the subject loan and emit any configured
          obligations to the waterfall engine. The action is recorded as the
          case&apos;s terminal step.
        </p>
        <p className="text-caption text-gray-mid m-0">
          Re-running the action on an already-approved case is a safe no-op
          (the prior result is returned).
        </p>
        <div className="flex items-center justify-end gap-space-2 mt-space-2">
          <Button variant="tertiary" onClick={onCancel}>Cancel</Button>
          <Button onClick={onConfirm} disabled={busy}>
            Confirm & approve
          </Button>
        </div>
      </div>
    </div>
  );
}
